import type { ReactNode } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import styles from './PageTransition.module.scss'

interface PageTransitionProps {
  children: ReactNode
}

/**
 * Per-route page-enter animation. Wraps the routed `<Outlet />` and
 * remounts the inner element on every navigation by keying off
 * `location.key`. The `data-direction` attribute drives one of two
 * keyframe variants:
 *
 *   POP            → "back"    (slide in from the LEFT)
 *   PUSH / REPLACE → "forward" (slide in from the RIGHT, default)
 *
 * Reduced-motion users get the new content with no animation.
 */
export function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const direction = navigationType === 'POP' ? 'back' : 'forward'

  return (
    <div
      key={location.key}
      className={styles.page}
      data-direction={direction}
    >
      {children}
    </div>
  )
}
